import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { intlFormatPropTypes, useIntl } from "react-intl-compat";
import { getCurrencyCode } from "~/i18n/i18n.utils";
import type { AmenityOption } from "~/services/env.service";

import {
  ListingUseType,
  useCreateListingMutation,
} from "../../../../components/src/services/listings.service";

import {
  ActivateListingFormValues,
  buildActivateListingFormSchema,
} from "./ActivateListingForm/ActivateListingForm.schema";
import { LISTING_USE_TYPE_CONFIGS } from "./ActivateListingsPage.consts";

/**
 * Returns the useForm configuration for the activate listings form
 */
const getFormConfig = ({
  intl,
  useType,
  amenityOptions,
  isInstantBookEligible,
}: {
  intl: intlFormatPropTypes;
  useType: ListingUseType;
  amenityOptions: AmenityOption[];
  isInstantBookEligible: boolean;
}): Parameters<typeof useForm<ActivateListingFormValues>>[0] => {
  return {
    resolver: zodResolver(
      buildActivateListingFormSchema({
        intl,
        requiredAmenityIds:
          LISTING_USE_TYPE_CONFIGS.find((config) => config.useType === useType)
            ?.requiredAmenityIds ?? [],
        isInstantBookEligible,
      }),
    ),
    defaultValues: {
      cleaningFeeOption: "INCLUDED",
      instantBookAvailability: isInstantBookEligible ? "EVERYONE" : "NO_ONE",
      instantBookRequested: false,
      amenities: Object.fromEntries(
        amenityOptions.map((amenity) => [amenity.id, amenity.default]),
      ),
      customAmenities: {},
    },
  };
};

/**
 * Orchestrates the three use type forms on the ActivateListingsPage,
 * including instantiation, submission/validation, and listing creation.
 */
const useActivateListingsForms = (
  amenitiesByUseType: Record<ListingUseType, AmenityOption[]>,
  isInstantBookEligible: boolean,
) => {
  const intl = useIntl();

  const [createListing] = useCreateListingMutation();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [errorMsg, setErrorMsg] = React.useState<string>();

  const eventsForm = useForm<ActivateListingFormValues>(
    getFormConfig({
      intl,
      useType: "Event",
      amenityOptions: amenitiesByUseType.Event,
      isInstantBookEligible,
    }),
  );

  const offsiteForm = useForm<ActivateListingFormValues>(
    getFormConfig({
      intl,
      useType: "Off-Site",
      amenityOptions: amenitiesByUseType["Off-Site"],
      isInstantBookEligible,
    }),
  );

  const productionsForm = useForm<ActivateListingFormValues>(
    getFormConfig({
      intl,
      useType: "Production",
      amenityOptions: amenitiesByUseType.Production,
      isInstantBookEligible,
    }),
  );

  const formsByUseType = React.useMemo(
    () => ({
      Event: eventsForm,
      "Off-Site": offsiteForm,
      Production: productionsForm,
    }),
    [eventsForm, offsiteForm, productionsForm],
  );

  /**
   * Submits forms for all selected use types and creates the listing if all forms are valid.
   * Returns true on success, false on failure (validation or listing creation)
   */
  const submitAllForms = async (
    spaceId: string,
    selectedUseTypes: ListingUseType[],
    spaceCountry: string,
  ): Promise<boolean> => {
    setIsSubmitting(true);
    setErrorMsg(undefined);

    let isValid = true;

    // First we validate all selected use type forms concurrently
    await Promise.all(
      selectedUseTypes.map(async (useType) => {
        const form = formsByUseType[useType];
        // Clear errors for all forms before validation
        form.clearErrors();

        // Submit each form to trigger validation and mark it as submitted
        await form.handleSubmit(
          // The success callback is a no-op since we only care about validation here.
          () => {},
          () => {
            isValid = false;
          },
        )();
      }),
    );

    // exit if any forms are invalid
    if (!isValid) {
      setIsSubmitting(false);
      return false;
    }

    try {
      // All forms are valid
      // go ahead and create the listings for each selected use type
      await Promise.all(
        selectedUseTypes.map((useType) => {
          const formValues = formsByUseType[useType].getValues();

          const selectedAmenities = Object.entries(formValues.amenities)
            .filter(([, isSelected]) => isSelected)
            .map(([id]) => id);

          const selectedCustomAmenities = Object.entries(
            formValues.customAmenities,
          )
            .filter(([, isSelected]) => isSelected)
            .map(([name]) => name);

          return createListing({
            spaceId,
            payload: {
              status: "PENDING",
              use_name: useType,
              amenities: [
                ...new Set([...selectedAmenities, ...selectedCustomAmenities]),
              ],
              price_currency: getCurrencyCode(spaceCountry),
              duration_type: "HOURLY_RATE",
              price_value: String(formValues.hourlyRate),
              minimum_duration: String(formValues.minimumDuration),
              number_guests: String(formValues.numberOfGuests),
              cleaning_fee:
                formValues.cleaningFeeOption === "FLAT_RATE"
                  ? {
                      obj_state: "ACTIVATED",
                      value: Number(formValues.cleaningFeeValue),
                      apply_as: "FLAT_RATE",
                    }
                  : {
                      obj_state: "DEACTIVATED",
                      apply_as: "FLAT_RATE",
                      value: 0,
                    },
              day_rate_discount: formValues.dayRateDiscountPercent
                ? {
                    obj_state: "ACTIVATED",
                    value: Number(formValues.dayRateDiscountPercent),
                    apply_as: "PERCENT_OFF",
                  }
                : {
                    obj_state: "DEACTIVATED",
                    apply_as: "PERCENT_OFF",
                    value: 0,
                  },
              is_instant_book_enabled: isInstantBookEligible
                ? formValues.instantBookAvailability === "EVERYONE"
                : formValues.instantBookRequested,
            },
          }).unwrap();
        }),
      );

      setIsSubmitting(false);
      return true;
    } catch {
      setIsSubmitting(false);
      setErrorMsg(
        intl.formatMessage({
          id: "activateListings.submitError",
          defaultMessage: "Something went wrong. Please try again.",
        }),
      );
      return false;
    }
  };

  return {
    formsByUseType,
    submitAllForms,
    isSubmitting,
    errorMsg,
  };
};

export { useActivateListingsForms };
